//Proofed
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BookDemoModal } from '../components/BookDemoModal'
import { Box, Container, Typography, Grid, Card, CardContent, Button, Stack, Chip, Divider } from '@mui/material'
import { FancyGoldButton } from '../components/FancyGoldButton'
import { FancyLightButton } from '../components/FancyLightButton'
import { PricingTierCard } from '../components/PricingTierCard'
import PageMeta from '../components/PageMeta'
import CheckIcon from '@mui/icons-material/Check'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PhoneIcon from '@mui/icons-material/Phone'
import { COLOR_PRIMARY, NAVBAR_BG, DARK_700, BG_LIGHT, COLOR_TERTIARY, BORDER_BLACK } from '../theme'
import {
    coreTiers, pricingFaqs, pricingTabConfig,
    tenderNinjaPricing,
    analyticsPricing,
    HERO_TAGLINE,
} from '../common/PricingContent'
import { buildCoreUrl, loginpage } from '../common/common'
import { tenderNinjaFeatures } from '../common/ProductTenderNinjaContent'
import { analyticsFeatures2 } from '../common/ProductAnalyticsContent'

export default function Pricing() {
    const [searchParams] = useSearchParams()
    const initialTab = Math.min(Math.max(Number(searchParams.get('tab') ?? 0), 0), pricingTabConfig.length - 1)
    const [activeTab, setActiveTab] = useState(initialTab)
    const [demoOpen, setDemoOpen] = useState(false)
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual')
    const resolveCtaHref = (href: string): string => {
        if (/^\/?login\.aspx$/i.test(href.trim())) return buildCoreUrl(loginpage)
        return href
    }

    return (
        <Box>
            <PageMeta
                title="Pricing"
                description="Simple, transparent pricing for UK & Ireland tender intelligence. CFP Core from £69/month. Tender Ninja AI bid writing and CFP Analytics available as add-ons. 14-day free trial."
                canonical="/pricing"
            />
            {/* Hero */}
            <Box sx={{
                background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_700} 100%)`,
                pt: { xs: 8, md: 12 },
                pb: { xs: 8, md: 10 },
                textAlign: 'center',
            }}>
                <Container maxWidth="md">
                    <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '48px' }, mb: 2 }}>
                        Simple Transparent Pricing
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '18px', mb: 3 }}>
                        {HERO_TAGLINE}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
                        <Chip
                            label="Call 0333 772 0250 to discuss pricing"
                            icon={<PhoneIcon sx={{ fontSize: '14px !important', color: `${COLOR_PRIMARY} !important` }} />}
                            sx={{ backgroundColor: 'rgba(244,182,50,0.15)', color: COLOR_PRIMARY, border: '1px solid rgba(244,182,50,0.3)', fontWeight: 600 }}
                        />
                    </Stack>
                </Container>
            </Box>

            {/* Tab panels */}
            <Box sx={{ backgroundColor: BG_LIGHT, pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 }, minHeight: 500 }}>
                <Container maxWidth="lg">
                    {/* Tab bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 4, md: 6 } }}>
                        <Box sx={{
                            display: 'inline-flex',
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            p: 0.5,
                            gap: 0.5,
                        }}>
                            {pricingTabConfig.map((tab, i) => {
                                const selected = activeTab === i
                                return (
                                    <Box
                                        key={tab.label}
                                        onClick={() => setActiveTab(i)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            px: { xs: 2, md: 3 }, py: 1.25,
                                            borderRadius: 1.5,
                                            cursor: 'pointer',
                                            backgroundColor: selected ? 'white' : 'transparent',
                                            border: selected ? `1.5px solid ${tab.color}50` : '1.5px solid transparent',
                                            boxShadow: selected ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                            transition: 'all 0.2s',
                                            userSelect: 'none',
                                            '&:hover': !selected ? { backgroundColor: 'rgba(255,255,255,0.6)' } : {},
                                        }}
                                    >
                                        <Box sx={{
                                            width: 28, height: 28, borderRadius: 1,
                                            backgroundColor: selected ? `${tab.color}18` : 'rgba(0,0,0,0.05)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'background-color 0.2s',
                                        }}>
                                            <tab.Icon sx={{ fontSize: 15, color: selected ? tab.color : 'text.disabled' }} />
                                        </Box>
                                        <Typography variant="body2" sx={{
                                            fontWeight: selected ? 700 : 400,
                                            color: selected ? tab.color : 'text.secondary',
                                            transition: 'color 0.2s',
                                            fontSize: { xs: '13px', md: '15px' },
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {tab.label}
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Box>

                    {/* ── Core Platform ── */}
                    {activeTab === 0 && (
                        <Box>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Typography variant="caption" sx={{ color: COLOR_PRIMARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Core Platform
                                </Typography>
                                <Typography variant="h3" sx={{ mt: 1, mb: 1 }}>Contract Finder Pro</Typography>
                                <Typography variant="body1" color="text.secondary">
                                    The UK & Ireland tender monitoring platform. Choose the tier that fits your team
                                </Typography>
                            </Box>

                            {/* Billing toggle */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '120px 52px 120px', sm: '140px 52px 140px' },
                                    gridTemplateRows: 'auto auto',
                                    columnGap: 0,
                                    rowGap: '2px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    mb: 5,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    onClick={() => setBillingCycle('annual')}
                                    sx={{
                                        gridColumn: 1,
                                        gridRow: 1,
                                        justifySelf: 'center',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        opacity: billingCycle === 'annual' ? 1 : 0.25,
                                        fontSize: '20px',
                                        lineHeight: 1.1,
                                        transition: 'opacity 0.2s',
                                    }}
                                >
                                    Yearly
                                </Typography>
                                <Box
                                    onClick={() => setBillingCycle(c => c === 'annual' ? 'monthly' : 'annual')}
                                    sx={{
                                        gridColumn: 2,
                                        gridRow: 1,
                                        justifySelf: 'center',
                                        width: 52, height: 28, borderRadius: 14,
                                        backgroundColor: COLOR_PRIMARY,
                                        position: 'relative', cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 3, left: billingCycle === 'monthly' ? 27 : 3,
                                        width: 22, height: 22, borderRadius: '50%',
                                        backgroundColor: 'white',
                                        transition: 'left 0.2s',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                    }} />
                                </Box>
                                <Typography
                                    variant="body2"
                                    onClick={() => setBillingCycle('monthly')}
                                    sx={{
                                        gridColumn: 3,
                                        gridRow: 1,
                                        justifySelf: 'center',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        opacity: billingCycle === 'monthly' ? 1 : 0.25,
                                        fontSize: '20px',
                                        lineHeight: 1.1,
                                        transition: 'opacity 0.2s',
                                    }}
                                >
                                    Monthly
                                </Typography>
                                <Chip
                                    label="2 months free"
                                    size="small"
                                    onClick={() => setBillingCycle('annual')}
                                    sx={{
                                        gridColumn: 1,
                                        gridRow: 2,
                                        justifySelf: 'center',
                                        height: '20px',
                                        cursor: 'pointer',
                                        backgroundColor: billingCycle === 'annual' ? `${COLOR_TERTIARY}18` : 'rgba(0,0,0,0.04)',
                                        color: billingCycle === 'annual' ? COLOR_TERTIARY : 'text.disabled',
                                        fontWeight: 700,
                                        border: billingCycle === 'annual' ? `1px solid ${COLOR_TERTIARY}40` : '1px solid rgba(0,0,0,0.12)',
                                        opacity: billingCycle === 'annual' ? 1 : 0.65,
                                        transition: 'all 0.2s',
                                        '& .MuiChip-label': {
                                            fontSize: '12px',
                                            lineHeight: '20px',
                                            px: 1.1,
                                        },
                                    }}
                                />
                            </Box>

                            <Grid container spacing={3} alignItems="stretch" justifyContent="center">
                                {coreTiers.map(tier => (
                                    <Grid item xs={12} md={4} key={tier.name}>
                                        <PricingTierCard
                                            tier={tier}
                                            billingCycle={billingCycle}
                                            resolveCtaHref={resolveCtaHref}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}


                    {/* ── Analytics ── */}
                    {activeTab === 1 && (
                        <Box>
                            <Box sx={{ textAlign: 'center', mb: 6 }}>
                                <Typography variant="caption" sx={{ color: analyticsPricing.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Market Intelligence
                                </Typography>
                                <Typography variant="h3" sx={{ mt: 1, mb: 1 }}>CFP Analytics</Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Deep insight into UK & Ireland public sector spending, pipeline and competition
                                </Typography>
                            </Box>
                            <Grid container spacing={3} justifyContent="center">
                                <Grid item xs={12} md={5}>
                                    <Card sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'visible',
                                        borderColor: analyticsPricing.color,
                                        borderWidth: 2,
                                        boxShadow: `0 8px 40px ${analyticsPricing.color}30`,
                                    }}>
                                        <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                                            <Chip label={analyticsPricing.badge} size="small" sx={{ backgroundColor: analyticsPricing.color, color: 'white', fontWeight: 700, px: 1 }} />
                                        </Box>
                                        <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="caption" sx={{ color: analyticsPricing.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                                CFP Analytics
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1, mb: 1 }}>
                                                <Typography sx={{ fontWeight: 800, fontSize: '42px', lineHeight: 1 }}>{analyticsPricing.price}</Typography>
                                                <Typography variant="caption">+VAT</Typography>

                                                <Typography color="text.secondary" variant="body1">{analyticsPricing.period}</Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                                                {analyticsPricing.tagline}
                                            </Typography>
                                            <Box sx={{ mb: 3 }}>
                                                <FancyGoldButton to="/contactus" color={analyticsPricing.color} fullWidth endIcon={<ArrowForwardIcon />}>
                                                    Contact Us
                                                </FancyGoldButton>
                                            </Box>
                                            <Divider sx={{ mb: 3 }} />
                                            <Stack spacing={1.5}>
                                                {analyticsFeatures2.map(f => (
                                                    <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                        <CheckIcon sx={{ fontSize: 18, color: COLOR_TERTIARY, flexShrink: 0, mt: 0.15 }} />
                                                        <Typography variant="body2">{f}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                    {/* ── Tender Ninja ── */}
                    {activeTab === 2 && (
                        <Box>
                            <Box sx={{ textAlign: 'center', mb: 6 }}>
                                <Typography variant="caption" sx={{ color: tenderNinjaPricing.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Safe AI Bid Writing
                                </Typography>
                                <Typography variant="h3" sx={{ mt: 1, mb: 1 }}>Tender Ninja</Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Write better bids faster
                                </Typography>
                            </Box>
                            <Grid container spacing={3} justifyContent="center">
                                <Grid item xs={12} md={5}>
                                    <Card sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'visible',
                                        borderColor: tenderNinjaPricing.color,
                                        borderWidth: 2,
                                        boxShadow: `0 8px 40px ${tenderNinjaPricing.color}30`,
                                    }}>
                                        <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                                            <Chip label={tenderNinjaPricing.badge} size="small" sx={{ backgroundColor: tenderNinjaPricing.color, color: 'white', fontWeight: 700, px: 1 }} />
                                        </Box>
                                        <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="caption" sx={{ color: tenderNinjaPricing.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                                Tender Ninja
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1, mb: 1 }}>
                                                {tenderNinjaPricing.price.startsWith('From ') ? (
                                                    <>
                                                        <Typography component="span" color="text.secondary" variant="body2" sx={{ mr: 0.25 }}>From</Typography>
                                                        <Typography sx={{ fontWeight: 800, fontSize: '42px', lineHeight: 1 }}>{tenderNinjaPricing.price.replace('From ', '')}</Typography>
                                                    </>
                                                ) : (
                                                    <Typography sx={{ fontWeight: 800, fontSize: '42px', lineHeight: 1 }}>{tenderNinjaPricing.price}</Typography>
                                                )}
                                                {/* <Typography sx={{ fontWeight: 800, fontSize: '42px', lineHeight: 1 }}>{tenderNinjaPricing.price}</Typography> */}
                                                <Typography variant="caption">+VAT</Typography>

                                                <Typography color="text.secondary" variant="body1">{tenderNinjaPricing.period}</Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                                                {tenderNinjaPricing.tagline}
                                            </Typography>
                                            <Box sx={{ mb: 3 }}>
                                                <FancyGoldButton to="/contactus" color={tenderNinjaPricing.color} fullWidth endIcon={<ArrowForwardIcon />}>
                                                    Contact Us
                                                </FancyGoldButton>
                                            </Box>
                                            <Divider sx={{ mb: 3 }} />
                                            <Stack spacing={1.5}>
                                                {tenderNinjaFeatures.map(f => (
                                                    <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                        <CheckIcon sx={{ fontSize: 18, color: COLOR_TERTIARY, flexShrink: 0, mt: 0.15 }} />
                                                        <Typography variant="body2">{f}</Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}


                </Container>
            </Box>

            {/* FAQs */}
            <Box sx={{ backgroundColor: 'white', py: { xs: 10, md: 12 } }}>
                <Container maxWidth="md">
                    <Typography variant="h3" sx={{ textAlign: 'center', mb: 6 }}>Frequently Asked Questions</Typography>
                    <Grid container spacing={4}>
                        {pricingFaqs.map(faq => (
                            <Grid item xs={12} sm={6} key={faq.q}>
                                <Typography variant="h6" sx={{ mb: 1 }}>{faq.q}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{faq.a}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* CTA */}
            <Box sx={{ backgroundColor: BG_LIGHT, py: { xs: 8, md: 10 }, textAlign: 'center' }}>
                <Container maxWidth="sm">
                    <Typography variant="h3" sx={{ mb: 2 }}>Still have questions?</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        Our team is happy to walk you through the platform and help find the <br />right plan for your business
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <FancyLightButton onClick={() => setDemoOpen(true)} color={COLOR_PRIMARY}>
                            Book a Demo
                        </FancyLightButton>
                        <Button variant="outlined" size="large" href="tel:03337720250" startIcon={<PhoneIcon />}>
                            0333 772 0250
                        </Button>
                    </Stack>
                </Container>
            </Box>
            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
